import { useEffect, useRef } from "react";
import { gsap, EASE, DUR, STAGGER, DISTANCE, START } from "./gsap";
import { prefersReducedMotion } from "./prefersReducedMotion";

interface RevealOptions {
  // CSS selector (scoped to the container) for the elements to reveal.
  // When omitted, the container's direct children are revealed.
  selector?: string;
  // Gap between consecutive elements. 0 = all at once.
  stagger?: number;
  // Distance in px the elements travel upward while fading in.
  distance?: number;
  // Horizontal offset in px the elements travel while fading in. Positive =
  // slide in from the right, negative = from the left. When set, the vertical
  // rise is suppressed so it's a pure horizontal slide.
  x?: number;
  // Starting scale for a subtle zoom-in during the reveal. 1 = no scale.
  // Kept part of the same tween so the fade/rise/scale stay locked together.
  scale?: number;
  duration?: number;
  // Scroll position at which the reveal fires (ScrollTrigger `start` syntax).
  start?: string;
  // Extra delay before the first element animates.
  delay?: number;
  // Re-run whenever a value in this array changes (e.g. active tab content).
  deps?: unknown[];
}

/**
 * Scroll-triggered "fade + slide up" reveal, matching the reference site's feel.
 * Returns a ref to attach to the container element. Children (or `selector`
 * matches) start hidden and animate in — staggered — as the container scrolls
 * into view. Fully respects `prefers-reduced-motion`: content simply renders
 * in place, visible, with no animation.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: RevealOptions = {}
) {
  const containerRef = useRef<T>(null);
  const {
    selector,
    stagger = STAGGER,
    distance = DISTANCE,
    x = 0,
    scale = 1,
    duration = DUR,
    start = START,
    delay = 0,
    deps = [],
  } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targets = selector
      ? Array.from(container.querySelectorAll<HTMLElement>(selector))
      : (Array.from(container.children) as HTMLElement[]);

    if (targets.length === 0) return;

    // Reduced motion: leave everything visible, do nothing.
    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      // A horizontal `x` slide takes over from the default vertical rise.
      const fromY = x !== 0 ? 0 : distance;
      gsap.fromTo(
        targets,
        {
          autoAlpha: 0,
          y: fromY,
          ...(x !== 0 ? { x } : {}),
          ...(scale !== 1 ? { scale } : {}),
        },
        {
          autoAlpha: 1,
          y: 0,
          ...(x !== 0 ? { x: 0 } : {}),
          ...(scale !== 1 ? { scale: 1 } : {}),
          duration,
          delay,
          ease: EASE,
          stagger,
          scrollTrigger: {
            trigger: container,
            start,
            // Play on enter, reverse when scrolled fully back above — feels
            // alive when scrubbing up and down without being distracting.
            toggleActions: "play none none reverse",
          },
        }
      );
    }, container);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return containerRef;
}
